import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Badge } from "@bednbody/ui/components/badge";
import { StatTile } from "@bednbody/ui/components/stat-tile";
import type { Icon } from "@phosphor-icons/react";
import { TrendDownIcon, TrendUpIcon } from "@phosphor-icons/react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { ChartStatFlow } from "@/components/charts/chart-stat-flow";

interface KpiCardProps {
  label: string;
  /** Prefer a number so the Bklit NumberFlow block animates. Strings (e.g.
   * "5/8" ratios) render statically — the block can't tween a non-number. */
  value: string | number;
  icon: Icon;
  /** Prefix/suffix for numeric values (e.g. prefix="₱") — passed to the block. */
  prefix?: string;
  suffix?: string;
  /** % change vs yesterday. Omit when there is no history to compare against —
   * live head-counts (riders online, ongoing trips) get no fabricated trend. */
  deltaPct?: number | null;
  /** 7-day series for the mini sparkline; omit to hide it. */
  spark?: number[];
  hint?: string;
  tone?: "default" | "warning" | "success" | "danger";
  isLoading?: boolean;
}

export function KpiCard({
  label,
  value,
  icon,
  prefix,
  suffix,
  deltaPct,
  spark,
  hint,
  tone = "default",
  isLoading,
}: KpiCardProps) {
  if (isLoading) {
    return (
      <StatTile
        layout="compact"
        label={label}
        detail={hint ? <span className="relative z-10">{hint}</span> : undefined}
        icon={icon}
        tone={tone}
        adornment={<Skeleton className="h-4 w-12" />}
      >
        <Skeleton className="h-6 w-16" />
        <Skeleton className="mt-2 h-3 w-24" />
      </StatTile>
    );
  }

  const trendUp = deltaPct != null && deltaPct >= 0;
  const sparkData = (spark ?? []).map((v, i) => ({ i, v }));

  return (
    <StatTile
      layout="compact"
      label={label}
      detail={hint ? <span className="relative z-10">{hint}</span> : undefined}
      icon={icon}
      tone={tone}
      adornment={
        deltaPct != null ? (
          <Badge
            variant={trendUp ? "success" : "destructive"}
            className="relative z-10 tabular-nums"
          >
            {trendUp ? <TrendUpIcon className="size-3" /> : <TrendDownIcon className="size-3" />}
            {trendUp ? "+" : ""}
            {deltaPct.toFixed(0)}%
          </Badge>
        ) : null
      }
    >
      {/* Bklit stat block: animated NumberFlow value + label */}
      <div className="relative z-10 flex flex-col items-start">
        {typeof value === "number" ? (
          <ChartStatFlow
            value={value}
            prefix={prefix}
            suffix={suffix}
            valueClassName="text-2xl font-semibold leading-none"
          />
        ) : (
          <>
            <span className="text-2xl font-semibold leading-none tabular-nums text-foreground">
              {prefix}
              {value}
              {suffix}
            </span>
          </>
        )}
      </div>

      {sparkData.length > 1 ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-9 opacity-60">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient
                  id={`kpi-spark-${label.replace(/\W/g, "")}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke="var(--primary)"
                strokeWidth={1.5}
                fill={`url(#kpi-spark-${label.replace(/\W/g, "")})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </StatTile>
  );
}
